import { useMutation } from '@tanstack/react-query';
import {
	createSecurityValueOverride,
	deleteSecurityValueOverride,
	updateSecurityValueOverride,
} from '@dealstack/frontend/shared/dataExchange/services/valuations.service';
import {
	CreateSecurityValueOverrideDto,
	SecurityValueOverrideScope,
	UpdateSecurityValueOverrideDto,
} from '@dealstack/shared/lib/generated';
import { VALUATION_SAVE_MUTATION_KEY } from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';

interface UseSecurityValueOverrideMutationsArgs {
	dealId: string | undefined;
	valuationId: string | undefined;
	reloadSummary: () => Promise<unknown>;
}

export function useSecurityValueOverrideMutations({
	dealId,
	valuationId,
	reloadSummary,
}: UseSecurityValueOverrideMutationsArgs) {
	const saveMutationKey = [VALUATION_SAVE_MUTATION_KEY, valuationId ?? ''];

	const createOverrideMutation = useMutation({
		mutationKey: saveMutationKey,
		mutationFn: (data: CreateSecurityValueOverrideDto) =>
			createSecurityValueOverride({
				dealId: dealId ?? '',
				valuationId: valuationId ?? '',
				data: {
					securityId: data.securityId,
					overrideValue: data.overrideValue,
					overrideField: data.overrideField,
					overrideScope: data.overrideScope ?? SecurityValueOverrideScope.WHOLE_SECURITY,
					note: data.note,
					trancheKey: data.trancheKey,
				},
			}),
		onSuccess: () => reloadSummary(),
	});

	const updateOverrideMutation = useMutation({
		mutationKey: saveMutationKey,
		mutationFn: ({
			overrideId,
			data,
		}: {
			overrideId: string;
			data: UpdateSecurityValueOverrideDto;
		}) =>
			updateSecurityValueOverride({
				dealId: dealId ?? '',
				valuationId: valuationId ?? '',
				securityValueOverrideId: overrideId,
				data,
			}),
		onSuccess: () => reloadSummary(),
	});

	const deleteOverrideMutation = useMutation({
		mutationKey: saveMutationKey,
		mutationFn: (overrideId: string) =>
			deleteSecurityValueOverride({
				dealId: dealId ?? '',
				valuationId: valuationId ?? '',
				securityValueOverrideId: overrideId,
			}),
		onSuccess: () => reloadSummary(),
	});

	return {
		createOverride: async (data: CreateSecurityValueOverrideDto) => {
			await createOverrideMutation.mutateAsync(data);
		},
		updateOverride: async (overrideId: string, data: UpdateSecurityValueOverrideDto) => {
			await updateOverrideMutation.mutateAsync({ overrideId, data });
		},
		deleteOverride: async (overrideId: string) => {
			await deleteOverrideMutation.mutateAsync(overrideId);
		},
	};
}
